import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { DocumentClassificationSource } from "documents/classification/document-classification-source";
import { DocumentContentAccess } from "documents/classification/document-content-access";
import Document from "documents/document";
import { subjectMatterRefusal } from "documents/downloads/document-download-withholding";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";
import { useSession } from "users/__session/session-context";

/**
 * The chips, then who chose them, under the document's name (#6720). The chips stay on one line;
 * the column widens instead.
 */
const Cell = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: theme.spacing(0.25),
  minWidth: 0,
}));

const Chips = styled("span")(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  gap: theme.spacing(0.5),
}));

/**
 * Smaller and paler than a caption, because it qualifies the chips rather than competing with them.
 * Grey 600 is as pale as small text goes and still meets WCAG AA (4.6:1 on white).
 */
const Source = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(11),
  lineHeight: 1.2,
  color: theme.palette.grey[600],
}));

const noSubjectMatterExplanation =
  "Classified, and carries no subject matter. Reading its content needs no subject-matter "
  + "access.";

interface DocumentSubjectMattersProps {
  document: Document;
}

/**
 * The subject-matter labels that decide who may read a document's content, and who chose them
 * (#4030).
 *
 * Four cases look different on purpose. Labels show as chips. A classified document with no
 * label says "None", because that is a statement about its content. A document whose content the
 * reader may not read for its subject matter says "Restricted", and names the labels they lack
 * (#6992): an empty cell read as "carries no label". A document whose labels the reader was not
 * sent for any other reason - still processing - shows nothing here, because there is nothing true
 * to say; `DocumentProcessingState` explains the processing cases.
 *
 * Only a processed document's list is drawn. The API sends the list wherever the reader may read
 * the content, and where quarantine is not armed that includes a document still being processed -
 * whose list is empty because nothing has labelled it yet, not because it carries no label.
 *
 * The source line under the chips says who chose them, the classifier or a named person, so that
 * a correction is never read as the classifier's judgement and the classifier's is never read as a
 * person's.
 */
export default function DocumentSubjectMatters({ document }: DocumentSubjectMattersProps) {
  const session = useSession();
  const subjectMatters = document.subjectMatters;
  if (document.contentAccess === DocumentContentAccess.SubjectMatterRestricted) {
    return <RestrictedCell document={document} />;
  }
  // Nothing at all, not two unseen lines: under the name, a placeholder the height of a real cell
  // was empty space, and the file icon centered on it sat below the name (#6720).
  if (!subjectMatters || !document.isProcessed) return null;

  const source = describeSource(document, session.user?.id?.isEqualTo(document.classificationOverriddenByUserId) ?? false);

  return (
    <Cell>
      <Chips aria-label="Subject matter">
        {subjectMatters.length === 0 ? (
          <Tooltip title={noSubjectMatterExplanation}>
            <Chip size="small" variant="outlined" label="None" />
          </Tooltip>
        ) : (
          subjectMatters.map((subjectMatter) => (
            <Chip key={subjectMatter} size="small" label={DocumentSubjectMatter[subjectMatter]} />
          ))
        )}
      </Chips>
      {source && (
        <Tooltip title={source.explanation}>
          <Source className="DocumentSubjectMatters-source" variant="caption" noWrap tabIndex={0}>
            {source.label}
          </Source>
        </Tooltip>
      )}
    </Cell>
  );
}

/**
 * A document the reader may open and whose content they may not read: a chip that says so, and the
 * labels they lack under it, which is what a refused download would name.
 */
function RestrictedCell({ document }: DocumentSubjectMattersProps) {
  const refusal = subjectMatterRefusal(document.restrictedSubjectMatters);
  return (
    <Cell>
      <Chips aria-label="Subject matter">
        <Tooltip describeChild title={refusal}>
          <Chip
            size="small"
            variant="outlined"
            icon={<LockOutlinedIcon />}
            label="Restricted"
            tabIndex={0}
          />
        </Tooltip>
      </Chips>
      <Source variant="caption" noWrap>
        {document.restrictedSubjectMatters?.length
          ? `Needs ${document.restrictedSubjectMatters.map((subjectMatter) => DocumentSubjectMatter[subjectMatter]).join(" and ")} access`
          : "Needs subject-matter access"}
      </Source>
    </Cell>
  );
}

function describeSource(
  document: Document,
  setByReader: boolean,
): { label: string; explanation: string } | undefined {
  if (document.classificationSource === DocumentClassificationSource.Automatic) {
    return {
      label: "Set automatically",
      explanation: "The classifier chose this from the document's content.",
    };
  }
  if (document.classificationSource !== DocumentClassificationSource.ManualOverride) return undefined;

  const setBy = setByReader ? "you" : (document.classificationOverriddenByName ?? "a person");
  const setOn = document.classificationOverriddenAt
    ? ` on ${document.classificationOverriddenAt.format("MM/DD/YY")}`
    : "";
  return {
    label: `Set by ${setBy}`,
    explanation: `The classifier did not choose this. It was set by ${setBy}${setOn}.`,
  };
}
